import { useFocusFirstElement, useModalPortalTarget } from '@ardoq/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { useToggleHandler } from './useToggleHandler';
import { fontSize, fontWeight, lineHeight } from '@ardoq/design-tokens';
import { POPOVER_WINDOW_EDGE_MARGIN, POPOVER_WRAPPER_PADDING } from './consts';

export type PopoverPlacement =
  | {
      side: 'top' | 'bottom';
      overflow: 'left' | 'right' | 'center';
    }
  | {
      side: 'left' | 'right';
      overflow: 'top' | 'bottom' | 'center';
    };
export type PopoverSide = 'top' | 'bottom' | 'left' | 'right';
export type PopoverOverflow = 'left' | 'right' | 'top' | 'bottom' | 'center';

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/anchor-name
 */
export type HTMLElementWithAnchor = (HTMLElement | SVGElement) & {
  style: CSSStyleDeclaration & { anchorName?: string };
};

export const anchorNameForId = (id: string): string => `--anchor-${id}`;

/**
 * Checks if an element is visible in the DOM (has non-zero dimensions).
 * This is used to detect if a nested popover's anchor is ready.
 */
const isElementVisible = (element: Element): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/**
 * Waits for an element to become visible using requestAnimationFrame polling.
 * Returns a cleanup function to cancel the waiting.
 *
 * This is used to wait for nested popover anchors to become visible
 * before showing the popover.
 */
const waitForElementVisible = (
  element: Element,
  onVisible: VoidFunction,
  maxAttempts = 60 // ~1 second at 60fps
): VoidFunction => {
  let attempts = 0;
  let rafId: number | null = null;

  const check = () => {
    if (isElementVisible(element)) {
      onVisible();
      return;
    }

    attempts++;
    if (attempts < maxAttempts) {
      rafId = requestAnimationFrame(check);
    }
  };

  rafId = requestAnimationFrame(check);

  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
    }
  };
};

const usePopoverPositioning = (
  popoverRef: React.RefObject<HTMLDivElement | null>,
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  isOpen: boolean,
  placement: PopoverPlacement,
  shouldPortal: boolean
) => {
  const [isPositioned, setIsPositioned] = useState(false);

  useEffect(() => {
    const popover = popoverRef.current;
    const anchor = anchorRef.current;
    if (!popover || !anchor) return;

    // The popover <div> may initially be created in the SVG namespace (when
    // the trigger is an SVG child); that element lacks the HTML Popover API.
    // We detect this elsewhere and re-render via a portal — bail out here and
    // let the post-portal re-run call showPopover.
    if (typeof popover.showPopover !== 'function') return;

    let cancelWaitForAnchor: VoidFunction | null = null;
    const isCurrentlyOpen = popover.matches(':popover-open');

    if (isOpen && !isCurrentlyOpen) {
      // Wait for anchor to be visible before showing popover (handles nested popovers)
      const showPopoverWhenReady = () => {
        popover.showPopover();
        setIsPositioned(true);
      };

      if (isElementVisible(anchor)) {
        showPopoverWhenReady();
      } else {
        cancelWaitForAnchor = waitForElementVisible(
          anchor,
          showPopoverWhenReady
        );
      }
    } else if (!isOpen && isCurrentlyOpen) {
      popover.hidePopover();
      setIsPositioned(false);
    } else if (isOpen && isCurrentlyOpen) {
      setIsPositioned(true);
    }

    return () => {
      cancelWaitForAnchor?.();
    };
  }, [popoverRef, anchorRef, isOpen, placement, shouldPortal]);

  return { isPositioned };
};

const useEscPress = (onClose: VoidFunction, isOpen: boolean) => {
  // We store the onClose in a ref to avoid re-registering the listener on every render
  // This ensures multiple popovers can be closed with the same esc click, even if the
  // onClose function is not stable.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onCloseRef, isOpen]);
};

const useClickOutside = (
  popoverRef: React.RefObject<HTMLDivElement | null>,
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  isOpen: boolean,
  onClickOutside?: VoidFunction
) => {
  const onClickOutsideRef = useRef(onClickOutside);
  useEffect(() => {
    onClickOutsideRef.current = onClickOutside;
  }, [onClickOutside]);

  useEffect(() => {
    if (!isOpen || !onClickOutsideRef.current) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const popover = popoverRef.current;
      const anchor = anchorRef.current;
      if (!popover) return;

      const target = event.target as Node;

      // If the target is not connected to the DOM, ignore the click
      // This can happen when clicking on an element that is being removed, such as a button inside the popover
      if (!target.isConnected) {
        return;
      }

      // If the click is inside the popover, do nothing
      if (popover.contains(target)) {
        return;
      }

      // If the click is on the anchor element, let the anchor handle it
      if (anchor && anchor.contains(target)) {
        return;
      }

      // Click is outside both popover and anchor
      onClickOutsideRef.current?.();
    };

    // Small delay so that the click event that opened the popover doesn't immediately close it
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleDocumentClick);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [popoverRef, anchorRef, isOpen]);
};

const isInsideOverflowClippedContainer = (element: Element): boolean => {
  let parent = element.parentElement;
  while (parent && parent !== document.body) {
    const { overflowX, overflowY } = getComputedStyle(parent);
    if (
      overflowX === 'hidden' ||
      overflowX === 'clip' ||
      overflowY === 'hidden' ||
      overflowY === 'clip'
    ) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
};

/**
 * When the trigger is inside an <svg>, React renders this popover <div> in
 * the SVG namespace — which lacks the HTML Popover API (showPopover). And
 * CSS anchor-positioning via anchor-name on SVG children doesn't give a
 * reliable layout box.
 *
 * Additionally, some browsers (notably Firefox) do not correctly resolve
 * anchor-name for elements inside overflow:hidden/clip containers when the
 * popover is in the top layer.
 *
 * This hook handles both cases by using a proxy approach:
 *  - On popover ref-attach, detects the SVG namespace and flips state so
 *    the popover re-renders via createPortal into document.body (a real
 *    HTMLElement with the Popover API).
 *  - On anchor ref-attach, detects overflow-clipped ancestors.
 *  - When a proxy is needed, mirrors the anchor with an invisible HTML proxy
 *    in document.body. The proxy position is refreshed on each open so that
 *    scrolled rows always get accurate coordinates.
 *  - Otherwise, sets anchor-name directly on the anchor as usual.
 */
const useSvgCompatiblePopover = (
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>,
  id: string,
  isOpen: boolean
) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isSvgNamespace, setIsSvgNamespace] = useState(false);
  const [hasOverflowClippedAncestor, setHasOverflowClippedAncestor] =
    useState(false);
  const modalPortalTarget = useModalPortalTarget();
  // Prefer the modal's portal target (placed inside the modal's FocusScope) when
  // available, so focusable content inside the popover doesn't get yanked back
  // into the modal by FocusScope's containment.
  const portalTarget = modalPortalTarget ?? document.body;

  const shouldUseProxy = isSvgNamespace || hasOverflowClippedAncestor;

  const attachPopoverRef = useCallback((element: HTMLDivElement | null) => {
    popoverRef.current = element;
    if (element && typeof element.showPopover !== 'function') {
      setIsSvgNamespace(true);
    }
  }, []);

  useEffect(() => {
    if (hasOverflowClippedAncestor) return;
    const anchor = anchorRef.current;
    if (!anchor) return;
    if (isInsideOverflowClippedContainer(anchor)) {
      setHasOverflowClippedAncestor(true);
    }
  }, [anchorRef, hasOverflowClippedAncestor]);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const anchorName = anchorNameForId(id);

    if (shouldUseProxy) {
      if (!isOpen) return;
      const proxy: HTMLElementWithAnchor = document.createElement('div');
      proxy.setAttribute('aria-hidden', 'true');
      const rect = anchor.getBoundingClientRect();
      proxy.style.position = 'fixed';
      proxy.style.top = `${rect.top}px`;
      proxy.style.left = `${rect.left}px`;
      proxy.style.width = `${rect.width}px`;
      proxy.style.height = `${rect.height}px`;
      proxy.style.pointerEvents = 'none';
      proxy.style.opacity = '0';
      proxy.style.anchorName = anchorName;
      portalTarget.appendChild(proxy);
      return () => {
        proxy.remove();
      };
    }

    anchor.style.anchorName = anchorName;
    return () => {
      anchor.style.anchorName = '';
    };
  }, [anchorRef, id, shouldUseProxy, isOpen, portalTarget]);

  const renderPopover = useCallback(
    (node: React.ReactElement): React.ReactNode =>
      shouldUseProxy ? createPortal(node, portalTarget) : node,
    [shouldUseProxy, portalTarget]
  );

  return {
    popoverRef,
    attachPopoverRef,
    renderPopover,
    shouldUseProxy,
  };
};

type FunctionalProps = {
  isOpen: boolean;
  onClose: VoidFunction;
  onClickOutside?: VoidFunction;
  id: string;
  anchorRef: React.RefObject<HTMLElementWithAnchor | null>;
};

export type BaseProps = {
  placement: PopoverPlacement;
  /**
   * When true, automatically focuses the first focusable element within the popover content.
   * By default, popovers add content next in the tabbing order without auto-focusing.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Popover_API/Using#popover_accessibility_features
   */
  focusContent?: boolean;
  className?: string;
  role?: React.AriaRole;
};

export type Popover2025Props = FunctionalProps & BaseProps;

/**
 * Requires the following attributes to be set on the trigger element:
 * - aria-haspopup="dialog"
 * - aria-expanded={isOpen}
 * - aria-controls={id}
 */
export const Popover2025 = ({
  isOpen,
  onClose,
  onClickOutside,
  children,
  id,
  anchorRef,
  placement,
  focusContent,
  className,
  role,
  ...ariaProps
}: React.PropsWithChildren<Popover2025Props>) => {
  const { popoverRef, attachPopoverRef, renderPopover, shouldUseProxy } =
    useSvgCompatiblePopover(anchorRef, id, isOpen);

  useEscPress(onClose, isOpen);
  useToggleHandler(popoverRef, isOpen, onClose);
  useClickOutside(popoverRef, anchorRef, isOpen, onClickOutside);
  const { isPositioned } = usePopoverPositioning(
    popoverRef,
    anchorRef,
    isOpen,
    placement,
    shouldUseProxy
  );

  useFocusFirstElement({
    containerRef: popoverRef,
    shouldFocus: isPositioned && isOpen && Boolean(focusContent),
  });

  if (!isOpen) return null;

  return renderPopover(
    <StyledPopover
      ref={attachPopoverRef}
      popover="manual" // This attribute is causing warning in the browser console, but is actually valid
      id={id}
      role={role}
      data-react-aria-top-layer="true"
      {...ariaProps}
      $placement={placement}
      $anchorId={id}
      className={className}
    >
      {children}
    </StyledPopover>
  );
};

// For off-centre placements the arrow points at the anchor's centre. Inset the
// popover's near edge `POPOVER_WRAPPER_PADDING + POPOVER_WINDOW_EDGE_MARGIN` past
// that centre. StyledPopoverContainer insets the card from this positioning box
// by its cross-axis window-edge margin, so adding that on top of the content
// padding lands the arrow `POPOVER_WRAPPER_PADDING` from the card's corner,
// regardless of anchor size.
const ARROW_EDGE_INSET = `calc(anchor(center) - ${POPOVER_WRAPPER_PADDING + POPOVER_WINDOW_EDGE_MARGIN}px)`;

const anchorPositionStyles: Record<
  PopoverSide,
  Record<PopoverOverflow, string | undefined>
> = {
  top: {
    top: undefined,
    right: `top: auto; right: auto; bottom: anchor(top); left: ${ARROW_EDGE_INSET};`,
    bottom: undefined,
    left: `top: auto; right: ${ARROW_EDGE_INSET}; bottom: anchor(top); left: auto;`,
    center: 'top: auto; bottom: anchor(top); justify-self: anchor-center;',
  },
  right: {
    top: `top: auto; right: auto; bottom: ${ARROW_EDGE_INSET}; left: anchor(right);`,
    right: undefined,
    bottom: `top: ${ARROW_EDGE_INSET}; right: auto; bottom: auto; left: anchor(right);`,
    left: undefined,
    center:
      'top: anchor(center); right: auto; bottom: anchor(center); left: anchor(right); margin-block: auto; height: max-content;',
  },
  bottom: {
    top: undefined,
    right: `top: anchor(bottom); right: auto; bottom: auto; left: ${ARROW_EDGE_INSET};`,
    bottom: undefined,
    left: `top: anchor(bottom); right: ${ARROW_EDGE_INSET}; bottom: auto; left: auto;`,
    center: 'top: anchor(bottom); bottom: auto; justify-self: anchor-center;',
  },
  left: {
    top: `top: auto; right: anchor(left); bottom: ${ARROW_EDGE_INSET}; left: auto;`,
    right: undefined,
    bottom: `top: ${ARROW_EDGE_INSET}; right: anchor(left); bottom: auto; left: auto;`,
    left: undefined,
    center:
      'top: anchor(center); right: anchor(left); bottom: anchor(center); left: auto; margin-block: auto; height: max-content;',
  },
};

const StyledPopover = styled.div<{
  $placement: PopoverPlacement;
  $anchorId: string;
}>`
  /** Removes default styling */
  margin: 0;
  border: none;
  overflow: visible;
  padding: 0;
  background-color: transparent;
  font-size: ${fontSize.regular};
  font-weight: ${fontWeight.regular};
  line-height: ${lineHeight.regular};
  /* Lets descendants query which position-try fallback is active via
     @container anchored(fallback: ...). */
  /* stylelint-disable-next-line declaration-property-value-no-unknown */
  container-type: anchored;

  ${({ $placement, $anchorId }) => `
    position-anchor: ${anchorNameForId($anchorId)};
    position-try-fallbacks: ${toPositionFallback($placement)};
    ${anchorPositionStyles[$placement.side][$placement.overflow]}
  `}
`;

const toPositionFallback = (
  placement: PopoverPlacement
): React.CSSProperties['positionTryFallbacks'] => {
  const { side, overflow } = placement;

  if (side === 'top' || side === 'bottom') {
    if (overflow === 'center') {
      return 'flip-block';
    }
    return 'flip-block, flip-inline, flip-block flip-inline';
  }

  if (overflow === 'center') {
    return 'flip-inline';
  }
  return 'flip-inline, flip-block, flip-block flip-inline';
};
